/**
 * Physics
 * ───────
 * AABB collision + trigger system with spatial hashing.
 *
 * Setup:
 *   physics.register('player')
 *   physics.register('environment')
 *   physics.register('action')
 *   physics.collides('player', 'environment')
 *   physics.collides('player', 'action')
 *
 * Usage:
 *   const box = physics.add(x, y, { x: -0.3, y: 0, w: 0.6, h: 0.4 }, 'player', false, entity)
 *   box.move(dx, dy)          // resolves against solid (non-trigger) boxes in matrix
 *   box.query()               // returns overlapping boxes in matrix
 *   physics.nearest(box, 'action', 3.0)
 *   physics.queryRect(x, y, w, h, 'hurtbox')
 *
 * Callbacks (fired during update()):
 *   box.onContactStart = (other) => {}   // solid contact started
 *   box.onContactEnd  = (other) => {}   // solid contact ended
 *   box.onTriggerEnter   = (other) => {}   // trigger overlap started
 *   box.onTriggerLeave    = (other) => {}   // trigger overlap ended
 */

export class Physics {
  constructor() {
    this._boxes = new Map()
    this._nextId = 0

    // type system — bitmask interaction matrix
    this._types = new Map()
    this._nextBit = 1
    this._matrix = new Map()

    // spatial grid
    this._cellSize = 2
    this._grid = new Map()

    // overlap tracking for enter/exit events
    // key: "smallId:largeId" → { a, b, trigger }
    this._activePairs = new Map()
    this._pairBuffer = new Map()

    // reusable
    this._result = { x: 0, y: 0 }
    this._seen = new Set()

    this._contacts = new Map() // pid -> { a, b } — solid contacts from move() this frame

    this.register('solid')
    this.register('character')
    this.register('probe')
    this.register('action')

    this.register('portal')

    this.collides('solid', 'solid')
    this.collides('character', 'solid')
    this.collides('probe', 'action')
    this.collides('portal', 'character')
  }

  // ── types ──

  register(name) {
    if (this._types.has(name)) return
    const bit = this._nextBit
    this._nextBit <<= 1
    this._types.set(name, bit)
    this._matrix.set(bit, 0)
  }

  collides(nameA, nameB) {
    const a = this._types.get(nameA)
    const b = this._types.get(nameB)
    this._matrix.set(a, this._matrix.get(a) | b)
    this._matrix.set(b, this._matrix.get(b) | a)
  }

  // ── spatial grid ──

  _key(cx, cy) {
    return cx * 73856093 + cy * 19349663
  }

  _cells(x, y, w, h) {
    const s = this._cellSize
    return {
      x0: Math.floor(x / s),
      y0: Math.floor(y / s),
      x1: Math.floor((x + w) / s),
      y1: Math.floor((y + h) / s),
    }
  }

  _gridInsert(box) {
    const { x0, y0, x1, y1 } = this._cells(box.x, box.y, box.w, box.h)
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const k = this._key(cx, cy)
        let cell = this._grid.get(k)
        if (!cell) {
          cell = new Set()
          this._grid.set(k, cell)
        }
        cell.add(box)
      }
    }
    box._gx0 = x0
    box._gy0 = y0
    box._gx1 = x1
    box._gy1 = y1
  }

  _gridRemove(box) {
    for (let cx = box._gx0; cx <= box._gx1; cx++) {
      for (let cy = box._gy0; cy <= box._gy1; cy++) {
        const k = this._key(cx, cy)
        const cell = this._grid.get(k)
        if (cell) {
          cell.delete(box)
          if (cell.size === 0) this._grid.delete(k)
        }
      }
    }
  }

  _gridUpdate(box) {
    const { x0, y0, x1, y1 } = this._cells(box.x, box.y, box.w, box.h)
    if (x0 !== box._gx0 || y0 !== box._gy0 || x1 !== box._gx1 || y1 !== box._gy1) {
      this._gridRemove(box)
      this._gridInsert(box)
    }
  }

  _nearby(x, y, w, h, mask) {
    const { x0, y0, x1, y1 } = this._cells(x, y, w, h)
    const seen = this._seen
    seen.clear()
    const results = []
    for (let cx = x0; cx <= x1; cx++) {
      for (let cy = y0; cy <= y1; cy++) {
        const cell = this._grid.get(this._key(cx, cy))
        if (!cell) continue
        for (const box of cell) {
          if (!seen.has(box) && box._typeBit & mask) {
            seen.add(box)
            results.push(box)
          }
        }
      }
    }
    return results
  }

  // ── overlap test ──

  _overlaps(ax, ay, aw, ah, bx, by, bw, bh) {
    return ax < bx + bw && ax + aw > bx && ay < by + bh && ay + ah > by
  }

  // ── pair tracking ──

  _pairId(a, b) {
    return a.id < b.id ? a.id + ':' + b.id : b.id + ':' + a.id
  }

  // ── core API ──

  add(px, py, def, data) {
    const id = this._nextId++
    const typeBit = this._types.get(def.type)
    const ox = def.ox
    const oy = def.oy
    const box = {
      id,
      x: px + ox,
      y: py + oy,
      w: def.w,
      h: def.h,
      _ox: ox,
      _oy: oy,
      _typeBit: typeBit,
      type: def.type,
      trigger: !!def.trigger,
      data: data ?? null,

      // callbacks
      onContactStart: null,
      onContactEnd: null,
      onTriggerEnter: null,
      onTriggerLeave: null,

      // grid bookkeeping
      _gx0: 0,
      _gy0: 0,
      _gx1: 0,
      _gy1: 0,

      setPosition: (px, py) => {
        box.x = px + ox
        box.y = py + oy
        this._gridUpdate(box)
      },

      move: (dx, dy) => this._move(box, dx, dy),

      query: () => this._query(box),

      destroy: () => {
        // fire exit events for active pairs
        for (const [pid, pair] of this._activePairs) {
          if (pair.a === box || pair.b === box) {
            const other = pair.a === box ? pair.b : pair.a
            if (pair.trigger) {
              box.onTriggerLeave?.(other.data)
              other.onTriggerLeave?.(box.data)
            } else {
              box.onContactEnd?.(other.data)
              other.onContactEnd?.(box.data)
            }
            this._activePairs.delete(pid)
          }
        }
        this._gridRemove(box)
        this._boxes.delete(id)
      },
    }

    this._boxes.set(id, box)
    this._gridInsert(box)
    return box
  }

  _move(self, dx, dy) {
    const ox = self.x
    const oy = self.y
    const mask = this._matrix.get(self._typeBit) || 0

    // sweep area for broadphase
    const sx = dx > 0 ? ox : ox + dx
    const sy = dy > 0 ? oy : oy + dy
    const sw = self.w + Math.abs(dx)
    const sh = self.h + Math.abs(dy)

    const candidates = this._nearby(sx, sy, sw, sh, mask)

    // resolve X
    let nx = ox + dx
    let ny = oy
    for (let i = 0; i < candidates.length; i++) {
      const c = candidates[i]
      if (c === self || c.trigger) continue
      if (this._overlaps(nx, ny, self.w, self.h, c.x, c.y, c.w, c.h)) {
        if (dx > 0) nx = c.x - self.w
        else if (dx < 0) nx = c.x + c.w
        this._addContact(self, c)
      }
    }

    // resolve Y
    ny = oy + dy
    for (let i = 0; i < candidates.length; i++) {
      const c = candidates[i]
      if (c === self || c.trigger) continue
      if (this._overlaps(nx, ny, self.w, self.h, c.x, c.y, c.w, c.h)) {
        if (dy > 0) ny = c.y - self.h
        else if (dy < 0) ny = c.y + c.h
        this._addContact(self, c)
      }
    }

    self.x = nx
    self.y = ny
    this._gridUpdate(self)

    this._result.x = nx - ox
    this._result.y = ny - oy
    return this._result
  }

  _addContact(a, b) {
    const pid = a.id < b.id ? a.id + ':' + b.id : b.id + ':' + a.id
    this._contacts.set(pid, { a, b })
  }

  _query(self) {
    const mask = this._matrix.get(self._typeBit) || 0
    const nearby = this._nearby(self.x, self.y, self.w, self.h, mask)
    const results = []
    for (let i = 0; i < nearby.length; i++) {
      const c = nearby[i]
      if (c === self) continue
      if (this._overlaps(self.x, self.y, self.w, self.h, c.x, c.y, c.w, c.h)) {
        results.push(c.data)
      }
    }
    return results
  }

  nearest(self, type, range) {
    const targetBit = this._types.get(type)
    const cx = self.x + self.w * 0.5
    const cy = self.y + self.h * 0.5
    const nearby = this._nearby(self.x - range, self.y - range, self.w + range * 2, self.h + range * 2, targetBit)

    let best = null
    let bestDist = range * range
    for (let i = 0; i < nearby.length; i++) {
      const c = nearby[i]
      if (c === self) continue
      const dx = c.x + c.w * 0.5 - cx
      const dy = c.y + c.h * 0.5 - cy
      const d = dx * dx + dy * dy
      if (d < bestDist) {
        bestDist = d
        best = c
      }
    }
    return best
  }

  queryRect(x, y, w, h, type) {
    const targetBit = this._types.get(type)
    const nearby = this._nearby(x, y, w, h, targetBit)
    const results = []
    for (let i = 0; i < nearby.length; i++) {
      const c = nearby[i]
      if (this._overlaps(x, y, w, h, c.x, c.y, c.w, c.h)) {
        results.push(c)
      }
    }
    return results
  }

  getBoxes() {
    return this._boxes.values()
  }

  // ── update: pair tracking + enter/exit events ──

  update() {
    const buf = this._pairBuffer
    buf.clear()

    // solid contacts come from move() — already collected in this._contacts
    for (const [pid, pair] of this._contacts) {
      buf.set(pid, { a: pair.a, b: pair.b, trigger: false })
    }
    this._contacts.clear()

    // trigger overlaps come from spatial query
    for (const a of this._boxes.values()) {
      if (!a.trigger) continue
      const mask = this._matrix.get(a._typeBit) || 0
      if (mask === 0) continue

      const nearby = this._nearby(a.x, a.y, a.w, a.h, mask)
      for (let i = 0; i < nearby.length; i++) {
        const b = nearby[i]
        if (b.trigger && b.id <= a.id) continue
        if (!this._overlaps(a.x, a.y, a.w, a.h, b.x, b.y, b.w, b.h)) continue

        const pid = a.id + ':' + b.id
        buf.set(pid, { a, b, trigger: true })
      }
    }

    // enter/exit diffing stays the same
    for (const [pid, pair] of buf) {
      if (!this._activePairs.has(pid)) {
        if (pair.trigger) {
          pair.a.onTriggerEnter?.(pair.b.data)
          pair.b.onTriggerEnter?.(pair.a.data)
        } else {
          pair.a.onContactStart?.(pair.b.data)
          pair.b.onContactStart?.(pair.a.data)
        }
      }
    }

    for (const [pid, pair] of this._activePairs) {
      if (!buf.has(pid)) {
        if (pair.trigger) {
          pair.a.onTriggerLeave?.(pair.b.data)
          pair.b.onTriggerLeave?.(pair.a.data)
        } else {
          pair.a.onContactEnd?.(pair.b.data)
          pair.b.onContactEnd?.(pair.a.data)
        }
      }
    }

    const temp = this._activePairs
    this._activePairs = this._pairBuffer
    this._pairBuffer = temp
  }

  /**
   * DDA raycast through the spatial grid.
   * Returns the nearest hit, or null if nothing was hit.
   *
   *   const hit = physics.raycast(ax, ay, bx, by, 'solid', ignore)
   *   // hit = { box, data, t, x, y, nx, ny }
   *   //   t    = 0-1 fraction along the ray
   *   //   x,y  = world position of impact
   *   //   nx,ny = hit normal (-1, 0, or 1)
   *
   *   // LOS check:
   *   const blocked = physics.raycast(ax, ay, bx, by, 'solid', myBox) !== null
   */
  raycast(ax, ay, bx, by, typeName = 'solid', ignoreSet = null) {
    const bit = this._types.get(typeName)
    if (!bit) return null

    const dx = bx - ax
    const dy = by - ay
    const dist = Math.sqrt(dx * dx + dy * dy)
    if (dist < 0.001) return null

    const s = this._cellSize

    let cx = Math.floor(ax / s)
    let cy = Math.floor(ay / s)
    const endCx = Math.floor(bx / s)
    const endCy = Math.floor(by / s)

    const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0
    const stepY = dy > 0 ? 1 : dy < 0 ? -1 : 0

    const tDeltaX = stepX !== 0 ? Math.abs(s / dx) : Infinity
    const tDeltaY = stepY !== 0 ? Math.abs(s / dy) : Infinity

    let tMaxX = stepX > 0 ? ((cx + 1) * s - ax) / dx : stepX < 0 ? (cx * s - ax) / dx : Infinity
    let tMaxY = stepY > 0 ? ((cy + 1) * s - ay) / dy : stepY < 0 ? (cy * s - ay) / dy : Infinity

    const maxSteps = Math.abs(endCx - cx) + Math.abs(endCy - cy) + 2
    const seen = this._seen
    seen.clear()

    let bestHit = null
    let bestT = 1

    for (let i = 0; i < maxSteps; i++) {
      const cell = this._grid.get(this._key(cx, cy))
      if (cell) {
        for (const box of cell) {
          if (seen.has(box)) continue
          seen.add(box)
          if (ignoreSet?.has(box)) continue
          if (box.trigger) continue
          if (!(box._typeBit & bit)) continue

          const invDx = dx !== 0 ? 1 / dx : 1e10
          const invDy = dy !== 0 ? 1 / dy : 1e10

          let t1x = (box.x - ax) * invDx
          let t2x = (box.x + box.w - ax) * invDx
          let t1y = (box.y - ay) * invDy
          let t2y = (box.y + box.h - ay) * invDy

          if (t1x > t2x) {
            const tmp = t1x
            t1x = t2x
            t2x = tmp
          }
          if (t1y > t2y) {
            const tmp = t1y
            t1y = t2y
            t2y = tmp
          }

          const tEnter = Math.max(t1x, t1y)
          const tExit = Math.min(t2x, t2y)

          if (tEnter >= tExit || tExit <= 0 || tEnter >= bestT) continue

          const t = Math.max(tEnter, 0)

          let nx = 0,
            ny = 0
          if (t1x > t1y) {
            nx = dx > 0 ? -1 : 1
          } else {
            ny = dy > 0 ? -1 : 1
          }

          bestT = t
          bestHit = {
            box,
            data: box.data,
            t,
            x: ax + dx * t,
            y: ay + dy * t,
            nx,
            ny,
          }
        }
      }

      if (bestHit && tMaxX > bestT && tMaxY > bestT) break
      if (cx === endCx && cy === endCy) break

      if (tMaxX < tMaxY) {
        cx += stepX
        tMaxX += tDeltaX
      } else {
        cy += stepY
        tMaxY += tDeltaY
      }
    }

    return bestHit
  }
}
